// Create or rename a folder (F2 / FR-L1). One dialog for both: rename only ever touches the
// name; create also picks a scope (locked to the parent's scope when creating a subfolder) and,
// for "team", which team. The scope picker only offers what the caller can plausibly manage —
// Personal always, Org only for org admins/owners, Team whenever the org has a team — and lets
// the server's own 403/404-as-404 (folders.rs's `can_manage_folder`) be the last word on a team
// the caller doesn't actually admin.
import { useEffect, useState, type FormEvent } from "react";
import { Dialog, Select } from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import { createFolder, renameFolder } from "./api";
import type { FolderOut, FolderScope, ListResponse, OrgOut, TeamOut } from "../api/types";
import { Button, Field, Notice } from "../components/ui";

type Props =
  | {
      mode: "create";
      org: OrgOut;
      /** Set when creating a subfolder: scope and team are inherited, not chosen. */
      parent?: FolderOut;
      onClose: () => void;
      onSaved: (folder: FolderOut) => void;
    }
  | {
      mode: "rename";
      org: OrgOut;
      folder: FolderOut;
      onClose: () => void;
      onSaved: (folder: FolderOut) => void;
    };

export default function FolderFormDialog(props: Props) {
  const { org, onClose, onSaved } = props;
  const isRename = props.mode === "rename";
  const parent = props.mode === "create" ? props.parent : undefined;
  const isOrgAdmin = org.role === "owner" || org.role === "admin";

  const [name, setName] = useState(isRename ? props.folder.name : "");
  const [scope, setScope] = useState<FolderScope>(parent?.scope ?? "personal");
  const [teams, setTeams] = useState<TeamOut[]>([]);
  const [teamId, setTeamId] = useState(parent?.team_id ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only needed to populate the team picker for a top-level "team" scope folder.
  useEffect(() => {
    if (isRename || parent) return;
    api<ListResponse<TeamOut>>("GET", `/orgs/${org.id}/teams`)
      .then((res) => setTeams(res.items))
      .catch(() => {
        // Best-effort: the team option just won't have anything to pick if this fails.
      });
  }, [isRename, parent, org.id]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    setError(null);
    try {
      if (isRename) {
        const updated = await renameFolder(org.id, props.folder.id, trimmed);
        onSaved(updated);
        return;
      }
      if (scope === "team" && !teamId) {
        setError("Choose a team for this folder.");
        setSaving(false);
        return;
      }
      const created = await createFolder(org.id, {
        scope,
        name: trimmed,
        parent_id: parent?.id ?? null,
        team_id: scope === "team" ? teamId : null,
      });
      onSaved(created);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't save this folder.");
      setSaving(false);
    }
  }

  const title = isRename ? "Rename folder" : parent ? `New folder in "${parent.name}"` : "New folder";

  return (
    <Dialog open title={title} onClose={onClose} width={400}>
      <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 14, padding: "4px 24px 24px" }}>
        <Field
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          maxLength={120}
          required
        />
        {!isRename && !parent ? (
          <Select
            id="folder-scope"
            label="Where"
            value={scope}
            onChange={(v) => setScope(v as FolderScope)}
            options={[
              { value: "personal", label: "Personal — only you" },
              { value: "team", label: "Team — team members" },
              ...(isOrgAdmin ? [{ value: "org", label: "Org — everyone in the organization" }] : []),
            ]}
          />
        ) : null}
        {!isRename && !parent && scope === "team" ? (
          <Select
            id="folder-team"
            label="Team"
            placeholder="Choose a team…"
            value={teamId}
            onChange={setTeamId}
            options={teams.map((t) => ({ value: t.id, label: t.name }))}
          />
        ) : null}
        {error ? (
          <Notice tone="error">
            {error}
          </Notice>
        ) : null}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saving} disabled={!name.trim()}>
            {isRename ? "Rename" : "Create"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
